import type * as React from "react";
import { memo, type ReactNode, useEffect, useMemo } from "react";

import Parameter from "~/common/components/Parameter";
import classNames from "classnames";
import DateInput from "~/common/components/DateInput";
import { MAX_QUANTITY_LENGTH } from "~/utils/constants";
import { isOfferPriceValid, isValidQuantity } from "~/utils/packageEntryUtils";
import DeleteButton from "~/features/cart/activeQuote/components/productTable/DeleteButton";
import { CommentButton } from "~/common/components/CommentButton/CommentButton";

import PriceInput from "~/features/productDetails/components/AddToCartCard/SalesmanAddToCartCard/PriceInput";
import { type PriceData } from "~/domains/pricing/types/PriceData";
import * as PriceUtils from "~/utils/PriceUtils";
import CartQuantityInput from "~/features/cart/activeQuote/components/productTable/CartQuantityInput";
import { ActiveQuoteShowInternalContext } from "~/features/cart/activeQuote/components/ActiveQuoteEdit/ActiveQuoteEdit";
import SimplePriceText, { PriceTextVariants } from "~/domains/pricing/components/SimplePriceText/SimplePriceText";
import OptimeSolutionsMessage from "~/domains/optime/components/OptimeSolutionsMessage";
import { canUseCommentsForProduct } from "~/utils/productUtils";
import { Card } from "~/features/configurator/Linear/components/Card";
import { AliasesParameter } from "~/domains/product/components/ProductParams";
import { isSpecialPrice } from "~/features/productDetails/redux/productDetailsSelectors";
import { useOnMount } from "~/common/hooks/useOnMount";
import { Box, Collapse, Stack, styled } from "~/common/mui";
import CenterContents from "~/common/components/CenterContents";
import { PackagingSelection } from "~/domains/product/components/PackagingSelection/PackagingSelection";
import CircleSpinner from "~/common/components/CircleSpinner/CircleSpinner";
import { C_SURFACE } from "~/common/components/icon/Colors";
import { getTotalPrice, isProductPMA } from "~/domains/product/utils/productDescriptorUtils";
import { buildPackagingOptions } from "~/domains/product/utils/buildPackagingOptions";
import { obtainSpecialPriceLabel } from "~/domains/pricing/components/Price/v1/Price";
import { useTranslation } from "react-i18next";
import { InfoProductMessagePMA } from "~/features/productDetails/components/ProductSummary/InfoProductMessagePMA";

import { canSeePriceRefColumn } from "~/features/quickEntry/utils/helpers";
import { PriceReferenceParam } from "~/features/cart/cart/components/LineItemEditableParams";
import { usePriceReferenceSelection } from "~/app/config/useSession";

import { FCCTag } from "~/features/freeCapacityPush/components/FCCTag/FCCTag";
import { CommentInput } from "~/common/components/CommentInput/CommentInput";
import { Alert } from "~/common/mui/Alert/Alert";
import {
  findMinOrderQuantity,
  getDefaultQuantity,
  useMOQValidation,
} from "~/features/productDetails/components/ProductSummary/common/usePdpSummaryLogic";
import { isMinOrderQuantityRestrictionEnabled } from "~/features/cart/cart/utils/cartUtils";
import { EditablePackagingCellOld } from "~/features/cart/activeQuote/components/productTable/PackagingCellOld";
import { useSalesData } from "~/features/salesCampaign/hooks/useSalesData";
import type { QuotePackageEntryViewModel } from "~/features/cart/activeQuote/models/QuoteTableViewModel";

interface SelfProps {
  entryViewModel: QuotePackageEntryViewModel;
  index: number;
  onRender?: () => void;
  className?: string;
}

const CellRow = ({ children }: { children?: ReactNode }) => (
  <div className={"scha__active-quote__product-table__right-side__col-container"}>{children}</div>
);

const Cell: React.FunctionComponent<{
  alignEnd?: boolean;
  first?: boolean;
  last?: boolean;
  className?: string;
  sx?: object;
  children?: ReactNode;
}> = ({ children, alignEnd, first, last, className, sx }) => {
  const cls = classNames(
    "scha__cart__product-table__body-cell",
    {
      "first-on-right": first,
      "last-on-right": last,
      "flex-justify-end d-flex": alignEnd,
    },
    className,
  );
  return (
    <Box className={cls} sx={sx}>
      {children}
    </Box>
  );
};

const Row: React.FunctionComponent<{ className?: string; children?: ReactNode }> = ({ children, className }) => (
  <div className={classNames("d-flex", className)}>{children}</div>
);

const EditableLineItem = ({ entryViewModel, index, onRender }: SelfProps) => {
  const { t } = useTranslation();
  const { selection } = usePriceReferenceSelection();
  const packageEntry = entryViewModel;
  const product = entryViewModel.product;
  const packSize = packageEntry.packSize ?? 1;
  const minOrderQuantity = findMinOrderQuantity(product, packageEntry.packCode);
  const saleActive = packageEntry.saleCampaignEnabled;

  const { isPackageSizeInvalid, MOQHelperLabel } = useMOQValidation({
    quantity: packageEntry.quantity,
    packSize,
    minOrderQuantity,
    isAvailabilityLoaded: !!product.availability,
    isPackageSelected: !!packageEntry.packCode,
    isOnSale: saleActive,
  });
  const messages = packageEntry.messages.slice();

  const tLabel = (key: string) => t(`activeQuotePage.table.content.${key}`);
  const isCommentVisible = packageEntry.showComments;
  const quantityVisible = product.base.quantityVisible;
  const isOptime = product.base.isOptime;

  const { visiblePrice, targetTotal } = packageEntry;

  let offerPriceValid = true;
  if (packageEntry.priceOverriden && packageEntry.offerPrice) {
    offerPriceValid = isOfferPriceValid(packageEntry.offerPrice, packageEntry.msp);
  }

  const formatSalesPrice = (val?: PriceData) => (val ? PriceUtils.nonVolatileText(val) : "-");

  const priceUnit = visiblePrice.unit;
  const total = getTotalPrice(visiblePrice, packageEntry.quantity);

  const canSeeExternalComments = canUseCommentsForProduct(product.base.marketType);

  const {
    isDayDisabled,
    updateDate,
    updateComment,
    toggleShowComment,
    updateQty,
    removeEntry,
    updateInternalComment,
    updateTargetPrice,
    updateOfferPrice,
    blurOfferPrice,
    onPackagingClick,
    selectPackage,
    updatePriceReference,
  } = entryViewModel.actions;

  const disableEditing = !!packageEntry.messages.find((msg) => msg.type === "fatal");

  useOnMount(() => {
    if (!product.base.quantityVisible) {
      updateDate(new Date());
    }
  });

  useEffect(() => {
    if (onRender) {
      onRender();
    }
  }, [packageEntry, product, onRender]);

  const packageentryquantityinput = `package-entry-quantity-input-${index}`;

  const isSpecial = isSpecialPrice(product, packageEntry.packCode);
  const cspLabel = obtainSpecialPriceLabel(t);

  const isFcc = product.base.fifteenDigit[packageEntry.packCode]?.isFcc;
  const salesData = useSalesData();

  const isAddedFromQuote = packageEntry.quoteLine !== "" && packageEntry.quoteNumber !== "";
  const isMOQCheckEnabled = isMinOrderQuantityRestrictionEnabled() && !saleActive && !isAddedFromQuote && !isFcc;
  const packagingOptions = useMemo(
    () =>
      buildPackagingOptions(product.availability?.packagingInformation ?? [], product, salesData?.salePerPack ?? {}, t),
    [product, salesData?.salePerPack, t],
  );

  const validQty = !isMOQCheckEnabled
    ? isValidQuantity(packageEntry.packSize, packageEntry.quantity)
    : !isPackageSizeInvalid;

  const minQtyValue = isMOQCheckEnabled ? getDefaultQuantity(packSize, minOrderQuantity) : packSize;
  const qtyHelperLabel = isMOQCheckEnabled ? MOQHelperLabel : undefined;

  return (
    <ActiveQuoteShowInternalContext.Consumer>
      {(showDetails) => (
        <Card className={"mb-2 qpackageentry_" + index} disableHover={true}>
          <div className={"py-3"}>
            <CellRow>
              <Cell first={true} sx={{ flexFlow: "column", justifyContent: "space-between", position: "relative" }}>
                {quantityVisible ? (
                  <CartQuantityInput
                    className={"w-100 qquantity"}
                    label={t("input.quantity")}
                    value={packageEntry.quantity}
                    onChange={updateQty}
                    allowedLength={MAX_QUANTITY_LENGTH}
                    error={!validQty}
                    data-test-id={packageentryquantityinput}
                    step={packageEntry.packSize}
                    disabled={disableEditing}
                    helperLabel={qtyHelperLabel}
                    min={minQtyValue}
                  />
                ) : (
                  <Parameter
                    label={t("input.quantity")}
                    value={packageEntry.quantity}
                    data-test-id={"package-entry-quantity"}
                  />
                )}

                {isFcc && (
                  <Box sx={{ position: "relative", left: -16 }}>
                    <FCCTag anchor={"left"} />
                  </Box>
                )}
              </Cell>

              <Cell className={"d-flex flex-direction-column"}>
                <EditablePackagingCellOld
                  product={product}
                  lineItem={
                    {
                      packSize: packageEntry.packSize,
                      packCode: packageEntry.packCode,
                      quantity: packageEntry.quantity,
                      primaryWarehouseName: packageEntry.primaryWarehouseName,
                      source: packageEntry.source,
                    } as Parameters<typeof EditablePackagingCellOld>[0]["lineItem"]
                  }
                  openEdit={onPackagingClick}
                  isPackagingSectionOpen={packageEntry.isPackagingSelectionOpen}
                />
                <AliasesParameter
                  className={"mt-3"}
                  product={product.base}
                  mode={"15-digits"}
                  packCode={packageEntry.packCode}
                />
              </Cell>

              <Cell>
                {quantityVisible ? (
                  <DateInput
                    label={tLabel("chooseDate")}
                    data-test-id={"btn-edit-date"}
                    date={packageEntry.deliveryDate || null}
                    onChange={updateDate}
                    className={"quote_date_input quote_date_field"}
                    isInvalid={!packageEntry.deliveryDate}
                    isDayDisabled={isDayDisabled}
                    disabled={disableEditing}
                  />
                ) : (
                  <div>{"-"}</div>
                )}
              </Cell>

              <Cell>
                <Stack gap={1}>
                  <PriceInput
                    data-test-id={"package-entry-input-offer-price"}
                    label={t("addProduct.salesman.offerPrice")}
                    value={visiblePrice?.value}
                    onChange={updateOfferPrice}
                    onBlur={blurOfferPrice}
                    error={showDetails && !offerPriceValid}
                    helperLabel={
                      <>
                        <SimplePriceText
                          priceUnit={priceUnit}
                          variant={quantityVisible ? null : PriceTextVariants.monthlySubsription}
                        />
                        {isSpecial && <div className={"scha__customer-special-price"}>{cspLabel}</div>}
                      </>
                    }
                  />

                  <PriceReferenceParam
                    product={product.base}
                    priceReference={packageEntry.priceReference}
                    onChange={updatePriceReference}
                    data-test-id={"package-entry-price-reference"}
                    disabled={disableEditing}
                    // disabled={disableEditing || saleActive}
                    editable={!packageEntry.quoteNumber && canSeePriceRefColumn(selection)}
                  />
                </Stack>
              </Cell>

              <Cell className={"d-flex flex-direction-column"}>
                <strong>{PriceUtils.text(total)}</strong>
                {!quantityVisible && (
                  <div className={"mt-1 f-label-color"}>
                    <SimplePriceText
                      priceUnit={priceUnit}
                      variant={quantityVisible ? null : PriceTextVariants.yearlySubscription}
                    />
                  </div>
                )}
              </Cell>

              {showDetails && (
                <>
                  <Cell className={"scha__active-quote__internal-cell d-flex flex-direction-column"}>
                    <div className="scha__active-quote__internal-cell__bg" />
                    <div className="mt-0 mb-1">
                      <span className={"f-label-color mr-2"}>{t("activeQuotePage.table.label.csp")}</span>
                      <span data-test-id={"package-entry-net-price"}>{formatSalesPrice(packageEntry.netPrice)}</span>
                    </div>
                    <div className="my-1 mb-0">
                      <span className={"f-label-color mr-2"}>{t("activeQuotePage.table.label.rsp")}</span>
                      <span data-test-id={"package-entry-rsp"}>{formatSalesPrice(packageEntry.rsp)}</span>
                    </div>
                    {/* <div className="mt-1 mb-0">
                      <span className={"f-label-color mr-2"}>{t("activeQuotePage.table.label.msp")}</span>
                      <span>{formatSalesPrice(packageEntry.msp)}</span>
                    </div> */}
                  </Cell>

                  <Cell className={"scha__active-quote__internal-cell"}>
                    <div className="scha__active-quote__internal-cell__bg" />
                    <PriceInput
                      label={t("addProduct.salesman.targetPrice")}
                      data-test-id={"package-entry-input-target-price"}
                      value={packageEntry.targetPrice?.value}
                      onChange={updateTargetPrice}
                      helperLabel={
                        <SimplePriceText
                          priceUnit={priceUnit}
                          variant={quantityVisible ? null : PriceTextVariants.monthlySubsription}
                        />
                      }
                    />
                  </Cell>

                  <Cell className={"scha__active-quote__internal-cell"}>
                    <div className="scha__active-quote__internal-cell__bg" />
                    <strong data-test-id={"package-entry-total-price"}>{PriceUtils.text(targetTotal)}</strong>
                  </Cell>
                </>
              )}

              <Cell alignEnd={true} last={true}>
                <Stack gap={2} direction={"row"}>
                  <CommentButton
                    onClick={toggleShowComment}
                    open={packageEntry.showComments}
                    fontSize={"small"}
                    className={"scha__saved-configurations__comment-button"}
                  />
                  <DeleteButton onClick={removeEntry} disabled={!packageEntry.original} />
                </Stack>
              </Cell>
            </CellRow>

            {isCommentVisible && (
              <Row className={"mt-3"}>
                <CommentInput
                  className={"pl-3 pr-2 scha__active-quote__product-table__comment-input"}
                  label={t("activeQuotePage.comment.title")}
                  value={!canSeeExternalComments ? "" : packageEntry.comment || ""}
                  onChange={updateComment}
                  disabled={!canSeeExternalComments}
                />
                {showDetails && (
                  <div className={"pl-2 pr-3 scha__active-quote__product-table__internal-comment-input"}>
                    <div className="scha__active-quote__product-table__internal-comment-input__bg" />
                    <CommentInput
                      label={t("activeQuotePage.internalComment.title")}
                      value={packageEntry.internalComment || ""}
                      onChange={updateInternalComment}
                    />
                  </div>
                )}
              </Row>
            )}
            {/* spacer without border */}
            {(!quantityVisible || isOptime || messages.length > 0) && <div className={"mt-2 mb-0"} />}
            <Stack gap={1} padding={2}>
              {messages.length > 0 && (
                <div className="d-flex flex-wrap">
                  {messages.map((message) => (
                    <Alert
                      key={message.key}
                      severity={message.type === "fatal" ? "error" : (message.type as "warning" | "info" | "success")}
                      className="w-100"
                    >
                      {t(message.key)}
                    </Alert>
                  ))}
                </div>
              )}
              {isProductPMA(product) && <InfoProductMessagePMA />}
              {!quantityVisible && <Alert severity="info">{t("subscriptionProduct.DST.billingInfo")}</Alert>}
              {isOptime && <OptimeSolutionsMessage />}
            </Stack>
          </div>
          <Collapse in={packageEntry.isPackagingSelectionOpen}>
            <PackagingArea disableHover={true}>
              {product.isLoading && (
                <CenterContents className={"my-30"}>
                  <CircleSpinner />
                </CenterContents>
              )}
              {product.isError && <CenterContents className={"my-30"}>{t("hybrisAjaxError")}</CenterContents>}
              {product.isLoaded && (
                <PackagingSelection value={packageEntry.packCode} onChange={selectPackage} options={packagingOptions} />
              )}
            </PackagingArea>
          </Collapse>
        </Card>
      )}
    </ActiveQuoteShowInternalContext.Consumer>
  );
};

export default memo(EditableLineItem);

export const PackagingArea = styled(Card)({
  background: C_SURFACE,
  padding: "20px",
});
